import { useCallback, useEffect, useState } from "react";
import {
  AutoComplete,
  Layout,
  Badge,
  Button,
  Drawer,
  Dropdown,
  Empty,
  Menu,
  Tooltip,
  Tour,
  Segmented,
  Input,
  Modal,
  Pagination,
  Spin,
} from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChartOutlined,
  BellOutlined,
  BookOutlined,
  CameraOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  DeleteOutlined,
  FileSearchOutlined,
  LockOutlined,
  LogoutOutlined,
  MailOutlined,
  MenuOutlined,
  MessageOutlined,
  MoonOutlined,
  PhoneOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  SolutionOutlined,
  SunOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { changeTheme } from "../../actions/theme";
import { changeTimezone } from "../../actions/timezone";
import apiClient from "../../actions/api";
import { loadUser } from "../../actions/auth";
import {
  getEffectivePermissions,
  PAGE_DESCRIPTIONS,
  PAGE_LABELS,
} from "../../config/pagePermissions";

// import utils
import isEmpty from "../../validations/is-empty";
import getDate from "../../untils/getDate";
import getTime from "../../untils/getTime";

// import assets
import LogoSrc from "../../assets/image/logo.svg";

// import style
import "../../style/layout/header.scss";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

const { Header } = Layout;

/* Timezone selection now lives exclusively in Account Settings.
  {
    offset: "UTC-12:00",
    timezone: "Pacific/Pago_Pago",
    city: "American Samoa",
  },
  {
    offset: "UTC-11:00",
    timezone: "Pacific/Midway",
    city: "Niue, Samoa (old)",
  },
  { offset: "UTC-10:00", timezone: "Pacific/Honolulu", city: "Hawaii" },
  { offset: "UTC-09:00", timezone: "America/Anchorage", city: "Alaska" },
  {
    offset: "UTC-08:00",
    timezone: "America/Los_Angeles",
    city: "Pacific Time (US & Canada)",
  },
  {
    offset: "UTC-07:00",
    timezone: "America/Denver",
    city: "Mountain Time (US & Canada)",
  },
  {
    offset: "UTC-06:00",
    timezone: "America/Chicago",
    city: "Central Time (US & Canada)",
  },
  {
    offset: "UTC-05:00",
    timezone: "America/New_York",
    city: "Eastern Time (US & Canada)",
  },
  {
    offset: "UTC-04:00",
    timezone: "America/Santiago",
    city: "Chile, Atlantic Time",
  },
  {
    offset: "UTC-03:00",
    timezone: "America/Sao_Paulo",
    city: "Brazil, Argentina",
  },
  {
    offset: "UTC-02:00",
    timezone: "Atlantic/South_Georgia",
    city: "South Georgia",
  },
  { offset: "UTC-01:00", timezone: "Atlantic/Azores", city: "Azores" },
  {
    offset: "UTC±00:00",
    timezone: "Europe/London",
    city: "United Kingdom, Portugal",
  },
  {
    offset: "UTC+01:00",
    timezone: "Europe/Paris",
    city: "Central Europe, West Africa",
  },
  {
    offset: "UTC+02:00",
    timezone: "Europe/Athens",
    city: "Eastern Europe, South Africa",
  },
  {
    offset: "UTC+03:00",
    timezone: "Europe/Moscow",
    city: "Russia, East Africa",
  },
  { offset: "UTC+04:00", timezone: "Asia/Dubai", city: "UAE, Armenia" },
  { offset: "UTC+05:00", timezone: "Asia/Karachi", city: "Pakistan" },
  { offset: "UTC+05:30", timezone: "Asia/Kolkata", city: "India" },
  { offset: "UTC+06:00", timezone: "Asia/Dhaka", city: "Bangladesh" },
  { offset: "UTC+07:00", timezone: "Asia/Bangkok", city: "Thailand, Vietnam" },
  { offset: "UTC+08:00", timezone: "Asia/Singapore", city: "China, Singapore" },
  { offset: "UTC+09:00", timezone: "Asia/Tokyo", city: "Japan, Korea" },
  {
    offset: "UTC+10:00",
    timezone: "Australia/Sydney",
    city: "Eastern Australia",
  },
  {
    offset: "UTC+11:00",
    timezone: "Pacific/Guadalcanal",
    city: "Solomon Islands",
  },
  { offset: "UTC+12:00", timezone: "Pacific/Auckland", city: "New Zealand" },
  { offset: "UTC+13:00", timezone: "Pacific/Tongatapu", city: "Tonga" },
  {
    offset: "UTC+14:00",
    timezone: "Pacific/Kiritimati",
    city: "Line Islands (Kiribati)",
  },
*/

const HeaderSection = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);
  const permissionRoles = useSelector((state) => state.permissions.roles);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([location.pathname]);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [notificationFilter, setNotificationFilter] = useState("all");
  const [notificationPage, setNotificationPage] = useState(1);
  const [notificationTotal, setNotificationTotal] = useState(0);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [workspaceSearch, setWorkspaceSearch] = useState("");
  const [welcomeTourOpen, setWelcomeTourOpen] = useState(false);
  const [time, setTime] = useState(
    dayjs(dayjs().tz(timezoneMode.timezoneMode)).tz(
      timezoneMode.timezoneMode,
      true,
    ),
  );

  const currentPermissions = getEffectivePermissions(
    auth.user,
    permissionRoles,
  );
  const canAccess = (pageKey) => currentPermissions.includes(pageKey);
  const tourPages = currentPermissions
    .filter((permission) => PAGE_DESCRIPTIONS[permission])
    .map((permission) => ({
      key: permission,
      label: PAGE_LABELS[permission] || permission,
      description: PAGE_DESCRIPTIONS[permission],
    }));

  const workspaceOptions = [
    canAccess("dashboard") && { value: "/dashboard", label: "Dashboard" },
    canAccess("dashboard") && { value: "/applications", label: "Applications" },
    canAccess("dashboard") && { value: "/job-board", label: "Job Board" },
    canAccess("bid") && { value: "/bid", label: "Build resume" },
    canAccess("analyze") && {
      value: "/analyze",
      label: "Analyze",
    },
    canAccess("prompt") && { value: "/prompt", label: "AI Prompt" },
    canAccess("calls") && { value: "/calls", label: "Interviews" },
    canAccess("calendar") && { value: "/calendar", label: "Calendar" },
    canAccess("personalStatistic") && {
      value: "/personal-statistic",
      label: "Statistic",
    },
    canAccess("teamMates") && {
      value: "/team-mate",
      label: "Team",
    },
    canAccess("billing") && { value: "/billing", label: "Billing" },
    canAccess("accountSettings") && {
      value: "/account-settings",
      label: "Settings",
    },
    canAccess("teamCalls") && {
      value: "/team-interviews",
      label: "Team Interviews",
    },
    canAccess("teamCalendar") && {
      value: "/team-calendar",
      label: "Team Calendar",
    },
    canAccess("statistic") && { value: "/statistic", label: "Team Statistic" },
    canAccess("dailyReport") && {
      value: "/daily-report",
      label: "Daily Report",
    },
    Number(auth.user?.role) === 1 && {
      value: "/news",
      label: "News & Announcements",
    },
    canAccess("emailTemplate") && {
      value: "/email-template",
      label: "Email Templates",
    },
    Number(auth.user?.role) === 1 && {
      value: "/permissions",
      label: "Permissions",
    },
    Number(auth.user?.role) === 1 && {
      value: "/price-settings",
      label: "Price Settings",
    },
    Number(auth.user?.role) === 1 &&
      canAccess("jobBoardSettings") && {
        value: "/job-board-settings",
        label: "Job Board Settings",
      },
    canAccess("candidates") && { value: "/candidate", label: "Candidates" },
    canAccess("jobPosting") && { value: "/job-posting", label: "Job Posting" },
    canAccess("manageProfiles") && {
      value: "/manage-profiles",
      label: "Manage Profiles",
    },
    { value: "/pricing", label: "Pricing" },
    { value: "/games", label: "Games" },
    { value: "/guide", label: "Guide" },
  ].filter(Boolean);

  const mobileNavigationItems = [
    canAccess("billing") && {
      key: "/billing",
      label: <Link to="/billing">Billing</Link>,
      icon: <WalletOutlined />,
    },
    canAccess("accountSettings") && {
      key: "/account-settings",
      label: <Link to="/account-settings">Account Settings</Link>,
      icon: <SettingOutlined />,
    },
    canAccess("dashboard") && {
      key: "/dashboard",
      label: <Link to="/dashboard">Dashboard</Link>,
      icon: <BarChartOutlined />,
    },
    canAccess("dashboard") && {
      key: "/applications",
      label: <Link to="/applications">Applications</Link>,
      icon: <SolutionOutlined />,
    },
    canAccess("bid") && {
      key: "/bid",
      label: <Link to="/bid">Build Resume</Link>,
      icon: <FileSearchOutlined />,
    },
    canAccess("analyze") && {
      key: "/analyze",
      label: <Link to="/analyze">Analyze</Link>,
      icon: <SearchOutlined />,
    },
    canAccess("prompt") && {
      key: "/prompt",
      label: <Link to="/prompt">AI Prompt</Link>,
      icon: <MessageOutlined />,
    },
    canAccess("calls") && {
      key: "/calls",
      label: <Link to="/calls">Interviews</Link>,
      icon: <PhoneOutlined />,
    },
    canAccess("calendar") && {
      key: "/calendar",
      label: <Link to="/calendar">Calendar</Link>,
      icon: <CalendarOutlined />,
    },
    canAccess("personalStatistic") && {
      key: "/personal-statistic",
      label: <Link to="/personal-statistic">Statistic</Link>,
      icon: <BarChartOutlined />,
    },
    canAccess("teamMates") && {
      key: "/team-mate",
      label: <Link to="/team-mate">Team</Link>,
      icon: <UserOutlined />,
    },
    canAccess("teamCalls") && {
      key: "/team-interviews",
      label: <Link to="/team-interviews">Team Interviews</Link>,
      icon: <PhoneOutlined />,
    },
    canAccess("teamCalendar") && {
      key: "/team-calendar",
      label: <Link to="/team-calendar">Team Calendar</Link>,
      icon: <CalendarOutlined />,
    },
    canAccess("statistic") && {
      key: "/statistic",
      label: <Link to="/statistic">Team Statistic</Link>,
      icon: <BarChartOutlined />,
    },
    canAccess("dailyReport") && {
      key: "/daily-report",
      label: <Link to="/daily-report">Daily Report</Link>,
      icon: <BarChartOutlined />,
    },
    Number(auth.user?.role) === 1 && {
      key: "/news",
      label: <Link to="/news">News & Announcements</Link>,
      icon: <BellOutlined />,
    },
    canAccess("emailTemplate") && {
      key: "/email-template",
      label: <Link to="/email-template">Email Templates</Link>,
      icon: <MailOutlined />,
    },
    Number(auth.user?.role) === 1 && {
      key: "/permissions",
      label: <Link to="/permissions">Permissions</Link>,
      icon: <LockOutlined />,
    },
    Number(auth.user?.role) === 1 && {
      key: "/price-settings",
      label: <Link to="/price-settings">Price Settings</Link>,
      icon: <DollarOutlined />,
    },
    Number(auth.user?.role) === 1 &&
      canAccess("jobBoardSettings") && {
        key: "/job-board-settings",
        label: <Link to="/job-board-settings">Job Board Settings</Link>,
        icon: <SettingOutlined />,
      },
    canAccess("candidates") && {
      key: "/candidate",
      label: <Link to="/candidate">Candidates</Link>,
      icon: <SolutionOutlined />,
    },
    canAccess("jobPosting") && {
      key: "/job-posting",
      label: <Link to="/job-posting">Job Posting</Link>,
      icon: <FileSearchOutlined />,
    },
    {
      key: "/guide",
      label: <Link to="/guide">Guide</Link>,
      icon: <BookOutlined />,
    },
    {
      key: "/pricing",
      label: <Link to="/pricing">Pricing</Link>,
      icon: <DollarOutlined />,
    },
    {
      key: "/games",
      label: <Link to="/games">Games</Link>,
      icon: <PlayCircleOutlined />,
    },
    canAccess("profile") && {
      key: "/profile",
      label: <Link to="/profile">View Profile</Link>,
      icon: <UserOutlined />,
    },
    canAccess("manageProfiles") && {
      key: "/manage-profiles",
      label: <Link to="/manage-profiles">Manage Profiles</Link>,
      icon: <SettingOutlined />,
    },
    {
      label: <Link to="/logout">Sign Out</Link>,
      key: "/logout",
      icon: <LogoutOutlined />,
      danger: true,
    },
  ].filter(Boolean);

  const items = [
    canAccess("teamCalls") && {
      label: <Link to="/team-interviews">Team Interviews</Link>,
      key: "/team-interviews",
      icon: <PhoneOutlined />,
    },
    canAccess("teamCalendar") && {
      label: <Link to="/team-calendar">Team Calendar</Link>,
      key: "/team-calendar",
      icon: <CalendarOutlined />,
    },
    canAccess("statistic") && {
      label: <Link to="/statistic">Team Statistic</Link>,
      key: "/statistic",
      icon: <BarChartOutlined />,
    },
    canAccess("dailyReport") && {
      label: <Link to="/daily-report">Daily Report</Link>,
      key: "/daily-report",
      icon: <BarChartOutlined />,
    },
    Number(auth.user?.role) === 1 && {
      label: <Link to="/news">News & Announcements</Link>,
      key: "/news",
      icon: <BellOutlined />,
    },
    canAccess("emailTemplate") && {
      label: <Link to="/email-template">Email Templates</Link>,
      key: "/email-template",
      icon: <MailOutlined />,
    },
    Number(auth.user?.role) === 1 && {
      label: <Link to="/permissions">Permissions</Link>,
      key: "/permissions",
      icon: <LockOutlined />,
    },
    Number(auth.user?.role) === 1 && {
      label: <Link to="/price-settings">Price Settings</Link>,
      key: "/price-settings",
      icon: <DollarOutlined />,
    },
    Number(auth.user?.role) === 1 &&
      canAccess("jobBoardSettings") && {
        label: <Link to="/job-board-settings">Job Board Settings</Link>,
        key: "/job-board-settings",
        icon: <SettingOutlined />,
      },
    canAccess("candidates") && {
      label: <Link to="/candidate">Candidates</Link>,
      key: "/candidate",
      icon: <SolutionOutlined />,
    },
    canAccess("jobPosting") && {
      label: <Link to="/job-posting">Job Posting</Link>,
      key: "/job-posting",
      icon: <FileSearchOutlined />,
    },
    canAccess("screenshots") && {
      label: <Link to="/screenshots">Screenshots</Link>,
      key: "/screenshots",
      icon: <CameraOutlined />,
    },
    { type: "divider" },
    canAccess("manageProfiles") && {
      label: <Link to="/manage-profiles">Manage Profiles</Link>,
      key: "/manage-profiles",
      icon: <SettingOutlined />,
    },
    canAccess("profile") && {
      label: <Link to="/profile">View Profile</Link>,
      key: "/profile",
      icon: <UserOutlined />,
    },
    { type: "divider" },
    {
      label: <Link to="/logout">Sign Out</Link>,
      key: "/logout",
      icon: <LogoutOutlined />,
      danger: true,
    },
  ];

  const menuProps = {
    items,
    selectedKeys,
    onClick: ({ key }) => setSelectedKeys([key]),
  };

  useEffect(() => {
    // Whenever the route changes, update selectedKeys
    setSelectedKeys([location.pathname]);
  }, [location.pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(getDate(dayjs().tz(timezoneMode.timezoneMode)));
      setCurrentTime(getTime(dayjs().tz(timezoneMode.timezoneMode)));
      setTime(
        dayjs(dayjs().tz(timezoneMode.timezoneMode)).tz(
          timezoneMode.timezoneMode,
          true,
        ),
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [timezoneMode]);

  useEffect(() => {
    if (!isEmpty(localStorage.getItem("themeMode"))) {
      dispatch(
        changeTheme(
          localStorage.getItem("themeMode") === "dark" ? true : false,
        ),
      );
    } else {
      localStorage.setItem("themeMode", "light");
      dispatch(changeTheme(false));
    }

    if (!isEmpty(localStorage.getItem("timezoneMode"))) {
      dispatch(changeTimezone(localStorage.getItem("timezoneMode")));
    } else {
      localStorage.setItem("timezoneMode", "Europe/London");
    }
  }, [dispatch]);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?._id) return undefined;
    const loadUnreadCount = () => {
      apiClient
        .get(`/chats/unread-counts/${auth.user._id}`)
        .then((res) => setChatUnreadCount(res.data.total || 0))
        .catch(() => {});
    };
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [auth.isAuthenticated, auth.user?._id, location.pathname]);

  const loadNotifications = useCallback(async () => {
    if (!auth.isAuthenticated) return;
    setNotificationLoading(true);
    try {
      const { data } = await apiClient.get("/notifications", {
        params: {
          page: notificationPage,
          limit: 6,
          filter: notificationFilter,
        },
      });
      setNotifications(data.notifications || []);
      setNotificationUnreadCount(data.unreadCount || 0);
      setNotificationTotal(data.pagination?.total || 0);
    } catch (_error) {
      setNotifications([]);
    } finally {
      setNotificationLoading(false);
    }
  }, [auth.isAuthenticated, notificationFilter, notificationPage]);

  useEffect(() => {
    if (!auth.isAuthenticated) return undefined;
    const loadUnread = () => {
      apiClient
        .get("/notifications", { params: { page: 1, limit: 1 } })
        .then(({ data }) => setNotificationUnreadCount(data.unreadCount || 0))
        .catch(() => {});
    };
    loadUnread();
    const interval = window.setInterval(loadUnread, 30000);
    return () => window.clearInterval(interval);
  }, [auth.isAuthenticated]);

  useEffect(() => {
    if (notificationOpen) loadNotifications();
  }, [loadNotifications, notificationOpen]);

  const markAllNotificationsRead = async () => {
    if (!notificationUnreadCount) return;
    await apiClient.patch("/notifications/read-all");
    await loadNotifications();
  };

  const markNotificationRead = async (notification) => {
    if (notification.read) return;
    await apiClient.patch(`/notifications/${notification._id}/read`);
    await loadNotifications();
  };

  const deleteNotification = async (event, notificationId) => {
    event.stopPropagation();
    await apiClient.delete(`/notifications/${notificationId}`);
    if (notifications.length === 1 && notificationPage > 1)
      setNotificationPage((current) => current - 1);
    else await loadNotifications();
  };

  const respondToGroupInvitation = async (event, notification, response) => {
    event.stopPropagation();
    try {
      await apiClient.post(
        `/teams/invitations/${notification.metadata.invitationId}/respond`,
        { response },
      );
      await Promise.all([loadNotifications(), dispatch(loadUser())]);
    } catch (_error) {
      await loadNotifications();
    }
  };

  useEffect(() => {
    const permissionsReady =
      Array.isArray(auth.user?.page_permissions) || permissionRoles.length > 0;
    const pendingTour = localStorage.getItem("resumeBuilderPendingWelcomeTour");
    const belongsToCurrentUser =
      pendingTour === "true" ||
      pendingTour ===
        String(auth.user?.email || "")
          .trim()
          .toLowerCase();
    if (
      auth.isAuthenticated &&
      auth.user?._id &&
      permissionsReady &&
      belongsToCurrentUser
    ) {
      const timer = window.setTimeout(() => setWelcomeTourOpen(true), 600);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [auth.isAuthenticated, auth.user, permissionRoles.length]);

  const finishWelcomeTour = () => {
    localStorage.removeItem("resumeBuilderPendingWelcomeTour");
    setWelcomeTourOpen(false);
    if (
      canAccess("profile") &&
      (!auth.user?.template_url || !auth.user?.template_coverletter_url)
    ) {
      navigate("/profile");
    }
  };

  const handleChangeThemeMode = (checked) => {
    localStorage.setItem("themeMode", checked);
    dispatch(changeTheme(checked === "dark" ? true : false));
  };

  const seconds = time.second();
  const minutes = time.minute();
  const hours = time.hour() % 12;

  const secondHandRotation = seconds * 6; // 6 degrees per second (360/60)
  const minuteHandRotation = minutes * 6 + seconds * 0.1; // 0.1 degrees per second
  const hourHandRotation = hours * 30 + minutes * 0.5; // 30 degrees per hour + 0.5 per minute

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <Header
      className={`headerSection ${auth.isAuthenticated ? "authenticated-header" : "public-header"}`}
    >
      <Link to="/" className="main-logo">
        <img src={LogoSrc} alt="" aria-hidden="true" />
        <span className="brand-wordmark">
          Resume <strong>Builder</strong>
        </span>
      </Link>
      {auth.isAuthenticated && (
        <AutoComplete
          className="header-search"
          value={workspaceSearch}
          options={workspaceOptions}
          optionFilterProp="label"
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          onChange={setWorkspaceSearch}
          onSelect={(path) => {
            navigate(path);
            setWorkspaceSearch("");
          }}
        >
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search pages..."
            allowClear
          />
        </AutoComplete>
      )}
      <div className="menu-section">
        {auth.isAuthenticated && canAccess("billing") && (
          <Tooltip title="Available balance">
            <Link
              to="/billing"
              className="header-balance"
              aria-label={`Balance $${Number(auth.balance || 0).toFixed(3)}`}
            >
              <WalletOutlined />
              <div>
                <span>Balance</span>
                <strong>${Number(auth.balance || 0).toFixed(3)}</strong>
              </div>
            </Link>
          </Tooltip>
        )}
        <div className="clock" aria-hidden="true">
          <div className="clock-face">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="hour-mark"
                style={{ transform: `rotate(${i * 30}deg)` }}
              >
                <div className="hour-mark-inner"></div>
              </div>
            ))}

            <div
              className="hand hour-hand"
              style={{ transform: `rotate(${hourHandRotation}deg)` }}
            ></div>
            <div
              className="hand minute-hand"
              style={{ transform: `rotate(${minuteHandRotation}deg)` }}
            ></div>
            <div
              className="hand second-hand"
              style={{ transform: `rotate(${secondHandRotation}deg)` }}
            ></div>
            <div className="center-circle"></div>
          </div>
        </div>
        <div className="current-time-view">
          <ClockCircleOutlined className="digital-clock-icon" />
          <div className="digital-clock-copy">
            <div className="current-time-text">{currentTime}</div>
            <div className="current-date-text">{currentDate}</div>
          </div>
        </div>

        <>
          <Segmented
            className="theme-segmented"
            size="large"
            shape="round"
            options={[
              { value: "light", icon: <SunOutlined />, label: "Light" },
              { value: "dark", icon: <MoonOutlined />, label: "Dark" },
            ]}
            value={themeMode.themeMode ? "dark" : "light"}
            onChange={handleChangeThemeMode}
          />
        </>
        {!auth.isAuthenticated && (
          <>
            <Link
              className={`${location.pathname === "/games" ? "nav-item active" : "nav-item"} mobile-auth-link`}
              to="/games"
            >
              Games
            </Link>
            <Link
              className={`${location.pathname === "/pricing" ? "nav-item active" : "nav-item"} mobile-auth-link`}
              to="/pricing"
            >
              Pricing
            </Link>
          </>
        )}
        {auth.isAuthenticated ? (
          <>
            <Button
              className="mobile-menu-button"
              type="text"
              icon={<MenuOutlined />}
              aria-label="Open navigation menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
            />
            <Drawer
              className="mobile-navigation-drawer"
              title="Resume Builder"
              placement="right"
              width="min(88vw, 360px)"
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
            >
              <Menu
                mode="inline"
                selectedKeys={[location.pathname]}
                items={mobileNavigationItems}
                onClick={() => setMobileMenuOpen(false)}
              />
            </Drawer>
            <Tooltip title="Notifications">
              <Button
                type="text"
                className="header-notification-button"
                aria-label={`${notificationUnreadCount} unread notifications`}
                icon={
                  <Badge
                    count={notificationUnreadCount}
                    size="small"
                    overflowCount={99}
                  >
                    <BellOutlined />
                  </Badge>
                }
                onClick={() => setNotificationOpen(true)}
              />
            </Tooltip>
            {canAccess("teamChat") && (
              <Link
                className={
                  location.pathname === "/team-chat"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/team-chat"
              >
                <Tooltip title="Team chat">
                  <Badge
                    count={chatUnreadCount}
                    size="small"
                    overflowCount={99}
                  >
                    <MessageOutlined className="header-chat-icon" />
                  </Badge>
                </Tooltip>
              </Link>
            )}
            {canAccess("prompt") && (
              <Link
                className={
                  location.pathname === "/prompt"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/prompt"
              >
                AI Prompt
              </Link>
            )}
            {canAccess("calendar") && (
              <Link
                className={
                  location.pathname === "/calendar"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/calendar"
              >
                Calendar
              </Link>
            )}
            {canAccess("calls") && (
              <Link
                className={
                  location.pathname === "/calls"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/calls"
              >
                Calls
              </Link>
            )}
            {canAccess("bid") && (
              <Link
                className={
                  location.pathname === "/bid" ||
                  location.pathname === "/resume-preview"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/bid"
              >
                Bid
              </Link>
            )}
            {canAccess("analyze") && (
              <Link
                className={
                  location.pathname === "/analyze"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/analyze"
              >
                Analyze
              </Link>
            )}
            {canAccess("statistic") && (
              <Link
                className={
                  location.pathname === "/statistic"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/statistic"
              >
                Team Statistic
              </Link>
            )}
            {canAccess("personalStatistic") && (
              <Link
                className={
                  location.pathname === "/personal-statistic"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/personal-statistic"
              >
                Statistic
              </Link>
            )}
            {canAccess("dashboard") && (
              <Link
                className={
                  location.pathname === "/dashboard"
                    ? "nav-item active"
                    : "nav-item"
                }
                to="/dashboard"
              >
                Dashboard
              </Link>
            )}
            <Dropdown
              menu={menuProps}
              className="nv"
              trigger={["click"]}
              placement="bottomRight"
              overlayClassName="profile-dropdown"
              arrow={{ pointAtCenter: true }}
            >
              <Tooltip placement="left" title={auth?.user?.name}>
                <img className="avatar" src={auth?.user?.avatar} alt="avatar" />
              </Tooltip>
            </Dropdown>
          </>
        ) : (
          <>
            <Link
              className={
                location.pathname === "/login"
                  ? "nav-item mobile-auth-link active"
                  : "nav-item mobile-auth-link"
              }
              to="/login"
            >
              Sign In
            </Link>
            <Link
              className={
                location.pathname === "/register"
                  ? "nav-item mobile-auth-link active"
                  : "nav-item mobile-auth-link"
              }
              to="/register"
            >
              Sign Up
            </Link>
          </>
        )}
      </div>
      <Modal
        className="notification-modal"
        open={notificationOpen}
        onCancel={() => setNotificationOpen(false)}
        footer={null}
        width={520}
        centered
        title={
          <span className="notification-modal-title">
            <BellOutlined /> Notifications
          </span>
        }
      >
        <div className="notification-toolbar">
          <div className="notification-tabs" role="tablist">
            {["unread", "all"].map((filter) => (
              <button
                key={filter}
                type="button"
                role="tab"
                aria-selected={notificationFilter === filter}
                className={notificationFilter === filter ? "active" : ""}
                onClick={() => {
                  setNotificationFilter(filter);
                  setNotificationPage(1);
                }}
              >
                {filter === "all" ? "All" : "Unread"}
                {filter === "unread" && notificationUnreadCount > 0 && (
                  <span>{notificationUnreadCount}</span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="notification-read-all"
            disabled={!notificationUnreadCount}
            onClick={markAllNotificationsRead}
          >
            Mark all as read
          </button>
        </div>

        <Spin spinning={notificationLoading}>
          <div className="notification-list" aria-live="polite">
            {!notificationLoading && notifications.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  notificationFilter === "unread"
                    ? "You're all caught up"
                    : "No notifications yet"
                }
              />
            ) : (
              notifications.map((notification) => (
                <article
                  key={notification._id}
                  className={`notification-item ${notification.read ? "is-read" : "is-unread"}`}
                  onClick={() => markNotificationRead(notification)}
                >
                  <div className="notification-item-icon">
                    {notification.type === "group_invitation" ? (
                      <TeamOutlined />
                    ) : notification.type === "news" ? (
                      <BellOutlined />
                    ) : (
                      <WalletOutlined />
                    )}
                  </div>
                  <div className="notification-item-content">
                    <div className="notification-item-heading">
                      <strong>{notification.title}</strong>
                      <time dateTime={notification.createdAt}>
                        {dayjs(notification.createdAt).format(
                          "MMM D, YYYY · h:mm A",
                        )}
                      </time>
                    </div>
                    <p>{notification.message}</p>
                    {notification.type === "group_invitation" &&
                      notification.metadata?.status === "pending" &&
                      notification.metadata?.direction === "incoming" && (
                        <div className="notification-invite-actions">
                          <Button
                            size="small"
                            type="primary"
                            onClick={(event) =>
                              respondToGroupInvitation(
                                event,
                                notification,
                                "accept",
                              )
                            }
                          >
                            Accept
                          </Button>
                          <Button
                            size="small"
                            onClick={(event) =>
                              respondToGroupInvitation(
                                event,
                                notification,
                                "reject",
                              )
                            }
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                  </div>
                  {!(
                    notification.type === "group_invitation" &&
                    notification.metadata?.status === "pending"
                  ) && (
                    <button
                      type="button"
                      className="notification-delete"
                      aria-label={`Delete ${notification.title}`}
                      onClick={(event) =>
                        deleteNotification(event, notification._id)
                      }
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </article>
              ))
            )}
          </div>
        </Spin>

        {notificationTotal > 6 && (
          <Pagination
            className="notification-pagination"
            current={notificationPage}
            pageSize={6}
            total={notificationTotal}
            showSizeChanger={false}
            onChange={setNotificationPage}
          />
        )}
      </Modal>
      <Tour
        open={welcomeTourOpen}
        onClose={finishWelcomeTour}
        onFinish={finishWelcomeTour}
        steps={[
          {
            title: `Welcome, ${auth.user?.name || "there"}`,
            description:
              "Resume Builder only shows pages allowed for your role. Here is what your workspace includes.",
          },
          {
            title: "Your available pages",
            description: (
              <div className="role-tour-pages">
                {tourPages.map((page) => (
                  <p key={page.key}>
                    <strong>{page.label}:</strong> {page.description}
                  </p>
                ))}
              </div>
            ),
          },
          {
            title: "Set up document templates first",
            description:
              "Before building a resume, upload both resume and cover-letter DOCX templates in View Profile. Their variables are replaced with tailored AI-generated content.",
          },
        ]}
      />
    </Header>
  );
};

export default HeaderSection;
